import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'satala_update_dismissed_until';
const BUILD_HASH_KEY = 'satala_build_script_hash';
const ETAG_KEY = 'satala_app_etag';

export default function VersionUpdatePrompt() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [latestData, setLatestData] = useState({ etag: null, scriptHash: null });

  const isTemporarilyDismissed = () => {
    try {
      const until = sessionStorage.getItem(DISMISS_KEY);
      if (until && Number(until) > Date.now()) {
        return true;
      }
    } catch {}
    return false;
  };

  // Check if a new version is available on the server
  const checkForUpdates = useCallback(async () => {
    try {
      // In development mode (localhost with vite dev server), skip checking
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return;
      }

      if (isTemporarilyDismissed()) {
        return;
      }

      // 1. Check Service Worker waiting state
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update().catch(() => {});
          if (registration.waiting) {
            setHasUpdate(true);
            return;
          }
        }
      }

      // 2. Fetch fresh index.html with no-cache and compare the JS bundle name / hash
      const res = await fetch('/?_vcheck=' + Date.now(), {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
      });

      if (!res.ok) return;

      const html = await res.text();
      // Extract script src like /assets/index-XXXXX.js
      const scriptMatch = html.match(/src=["'](\/assets\/index-[^"']+\.js)["']/);
      const serverScriptHash = scriptMatch ? scriptMatch[1] : null;

      // Extract current loaded script hash from document
      const currentScript = document.querySelector('script[src*="/assets/index-"]');
      const currentScriptSrc = currentScript ? currentScript.getAttribute('src') : null;

      const storedScriptHash = localStorage.getItem(BUILD_HASH_KEY) || currentScriptSrc;

      if (serverScriptHash) {
        if (!localStorage.getItem(BUILD_HASH_KEY) && currentScriptSrc) {
          localStorage.setItem(BUILD_HASH_KEY, currentScriptSrc);
        }
        
        if (storedScriptHash && serverScriptHash !== storedScriptHash) {
          setLatestData({ scriptHash: serverScriptHash });
          setHasUpdate(true);
          return;
        }
      }

      // 3. Fallback: ETag check (only if script hashes cannot be parsed)
      const etag = res.headers.get('etag') || res.headers.get('last-modified');
      if (etag && !serverScriptHash) {
        const storedEtag = sessionStorage.getItem(ETAG_KEY);
        if (storedEtag && storedEtag !== etag) {
          setLatestData({ etag });
          setHasUpdate(true);
        } else if (!storedEtag) {
          sessionStorage.setItem(ETAG_KEY, etag);
        }
      }
    } catch {
      // Ignore network errors silently
    }
  }, []);

  useEffect(() => {
    // Initial check after 15s of load
    const timer = setTimeout(checkForUpdates, 15000);

    // Periodic check every 10 minutes
    const interval = setInterval(checkForUpdates, 10 * 60 * 1000);

    // Check when user returns to tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen to service worker controller changes
    const handleControllerChange = () => {
      setHasUpdate(false);
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, [checkForUpdates]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setHasUpdate(false);
    // Dismiss for 2 hours so it doesn't repeatedly annoy the user
    try {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 2 * 60 * 60 * 1000));
    } catch {}
  };

  const handleApplyUpdate = async () => {
    setIsUpdating(true);
    try {
      // Update the stored script hash / etag to latest before reload
      if (latestData.scriptHash) {
        localStorage.setItem(BUILD_HASH_KEY, latestData.scriptHash);
      }
      if (latestData.etag) {
        sessionStorage.setItem(ETAG_KEY, latestData.etag);
      }
      sessionStorage.removeItem(DISMISS_KEY);

      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Clear caches if available
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      }
    } catch {
      // Continue to reload anyway
    }

    // Smooth reload
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  if (!hasUpdate || isDismissed || isTemporarilyDismissed()) {
    return null;
  }

  return (
    <div className="fixed bottom-5 right-5 z-[9999] max-w-sm w-full bg-[#302621]/95 backdrop-blur-md text-white border border-[#E6D5B8]/30 rounded-2xl shadow-2xl p-4 animate-in slide-in-from-bottom-5 duration-300">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0 shadow-sm">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
              Pembaruan Sistem Siap! 🚀
            </h4>
            <p className="text-[11px] text-slate-300 mt-1 leading-snug">
              Versi terbaru Satala telah diunggah ke server. Terapkan sekarang untuk memuat fitur & perbaikan terbaru.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-slate-400 hover:text-white transition-colors p-1 cursor-pointer"
          title="Nanti saja"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3.5 pt-3 border-t border-white/10 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="h-8 text-xs text-slate-300 hover:text-white hover:bg-white/10 cursor-pointer"
        >
          Nanti Saja
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={isUpdating}
          onClick={handleApplyUpdate}
          className="h-8 text-xs font-bold bg-[#D4AF37] hover:bg-[#B38F24] text-[#302621] rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer"
        >
          {isUpdating ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Menerapkan...
            </>
          ) : (
            <>
              Terapkan Sekarang (0.3s)
              <ArrowRight className="w-3.5 h-3.5" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
